import React from "react";
import { FiClock, FiCoffee, FiLogIn, FiLogOut, FiCalendar } from "react-icons/fi";

function formatTime(timestamp) {
  if (!timestamp) return "--:--";
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDuration(minutes) {
  if (minutes == null) return "--:--";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const hrsPart = hrs > 0 ? `${hrs.toString().padStart(2, "0")}:` : "00:";
  return `${hrsPart}${mins.toString().padStart(2, "0")} min`;
}

const toneStyles = {
  emerald: "text-emerald-700 bg-emerald-50",
  teal: "text-teal-700 bg-teal-50",
  amber: "text-amber-700 bg-amber-50",
  orange: "text-orange-700 bg-orange-50",
  navy: "text-[#1a365d] bg-[#e8f0f4]",
};

function AttendanceCards({ attendanceData = [] }) {
  if (!attendanceData.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
        No attendance records yet. Check in to start tracking your day.
      </div>
    );
  }

  const grouped = attendanceData.reduce((acc, entry) => {
    const date = new Date(entry.timestamp).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  const latestDate = Object.keys(grouped).sort(
    (a, b) => new Date(b) - new Date(a)
  )[0];

  const latestEntries = grouped[latestDate] || [];

  const checkIn = latestEntries.find((entry) => entry.type === "check-in");
  const checkOut = latestEntries.find((entry) => entry.type === "check-out");

  let clockedTimeMinutes = null;
  if (checkIn && checkOut) {
    const diffMs =
      new Date(checkOut.timestamp).getTime() -
      new Date(checkIn.timestamp).getTime();
    if (diffMs > 0) {
      clockedTimeMinutes = Math.round(diffMs / (1000 * 60));
    }
  }

  const adminBreakMinutes = checkOut?.adminBreakTimeMinutes || 0;

  const cards = [
    {
      title: "Check in",
      value: formatTime(checkIn?.timestamp),
      note: checkIn ? "On time" : "Awaiting entry",
      tone: "emerald",
      icon: FiLogIn,
    },
    {
      title: "Check out",
      value: formatTime(checkOut?.timestamp),
      note: checkOut ? "Logged" : "Pending",
      tone: "teal",
      icon: FiLogOut,
    },
    {
      title: "Clocked time",
      value: formatDuration(clockedTimeMinutes),
      note: clockedTimeMinutes != null ? "Check in to check out" : "Pending",
      tone: "amber",
      icon: FiClock,
    },
    {
      title: "Admin break",
      value: formatDuration(adminBreakMinutes),
      note: adminBreakMinutes > 0 ? "Added by admin" : "No break added",
      tone: "orange",
      icon: FiCoffee,
    },
    {
      title: "Days logged",
      value: Object.keys(grouped).length.toString(),
      note: "This month",
      tone: "navy",
      icon: FiCalendar,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.title}
            className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {card.title}
              </p>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${toneStyles[card.tone]}`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
            <p className="mt-2 text-xl font-bold text-slate-800">{card.value}</p>
            <p className="mt-1 text-xs text-slate-500">{card.note}</p>
          </div>
        );
      })}
    </div>
  );
}

export default AttendanceCards;
